import { z } from 'zod';
import { assertNever } from './exhaustive';
import {
  type AggregateOp,
  aggregateOpSchema,
  type FieldType,
  isCountAggregateOp,
} from './field-types';
import { SELECT_OPTION_ID_PATTERN } from './ids';
import { parseRecordQuery, type QueryRecord, type RecordQuery } from './record-query';

/**
 * THE GROUPED READ'S VOCABULARY (`docs/decisions/group-by-aggregation.md`).
 *
 * Grouping records in a TABLE view, and aggregate values per group and per column,
 * computed in the query layer as one real `GROUP BY` statement. This file is what the
 * three consumers have to agree on (INV-11): the server plans, compiles and executes;
 * the browser drives the group sections, the footer and the cache key; and migration
 * 0015 creates the view columns these types are the domain of.
 *
 * TWO THINGS SIT HERE RATHER THAN WHERE THEY LOOK LIKE THEY BELONG.
 *
 * `bucketBounds` is one of them: a bucket key is a WALL CLOCK date in the dimension's
 * timezone, and a DATETIME filter operand is an instant with an offset. Converting
 * between the two is the conversion `views/validate.ts:190-191` has already refused to
 * make inside a pure function, so it gets ONE named home that the browser and the server
 * both call and therefore cannot disagree about (D13).
 *
 * The GroupKey codec is the other. A key is a typed tuple, never a joined string, and
 * where a single string is needed - a React key, a cache lookup, a collapse-state map -
 * it is produced by one exported function. One function, so there is no second encoding
 * to disagree with the first, and no separator to collide on (D5).
 *
 * WHAT IS DELIBERATELY NOT HERE: the capability tables. Which field types may be a
 * dimension and which aggregates each accepts are REGISTRY MEMBERS in `field-types.ts`,
 * beside `filterOperators`, because a `switch (fieldType)` in a new group module is
 * precisely what INV-12 exists to prevent (D14).
 */

// --- the caps (D4, D20) -----------------------------------------------------

/**
 * How many dimensions the QUERY LAYER takes. The UI ships one, and the second level is
 * the next step rather than a different feature: `group by <a>, <b>` is not harder than
 * `group by <a>`, so building for N costs one loop while retrofitting N costs a wire
 * type, a storage shape and a group key. Sized against Airtable's published ceiling of
 * three (one main group and two subgroups) - the widest any researched product offers.
 */
export const GROUP_MAX_DIMENSIONS = 3;

/** `RECORD_QUERY_DEFAULT_LIMIT`'s sibling, and Twenty's default too. */
export const GROUP_DEFAULT_LIMIT = 50;

/**
 * Well under `RECORD_QUERY_MAX_LIMIT` (500): a grouped statement is one scan, but 500
 * group headers is not a screen. It is also the board's ceiling - a SELECT needs fewer
 * than this many options to be a board at all (D23).
 *
 * Above the cap the group list TRUNCATES VISIBLY rather than refusing: a truncated list
 * is on a screen that can carry a sentence, next to the control that fixes it. That is
 * the one place this module reads `csv-import-export.md` D35's rule as being about
 * SILENCE rather than about truncation.
 */
export const GROUP_MAX_LIMIT = 200;

/**
 * A PREVIEW SIZE, NOT A FIRST PAGE (D19). Expanding a group discards the seeded rows and
 * mounts the ordinary infinite list from page 1, because the row statement ranks with
 * `row_number()` and therefore emits no cursor.
 *
 * Between the board's 25 and Twenty's grouped-TABLE 8. Arif's answer on 2026-08-16 was to
 * ship it and measure the first real grouped view, so it is the constant most likely to
 * move.
 */
export const ROWS_PER_GROUP_DEFAULT = 20;

/**
 * A SCHEMA CEILING ON HOW MANY ROWS ONE GROUPED STATEMENT MAY SEED PER GROUP. Zero is
 * legal and means "aggregates only".
 *
 * Sized at the DEFAULT page size (`RECORD_QUERY_DEFAULT_LIMIT`) because a preview larger
 * than a page is a preview pretending to be a list (D19: "`rowsPerGroup` is a PREVIEW
 * size, not a first page"). It is NOT the flat table's page size, and the reason is
 * `table-completion.md` D2 and D4: with a choosable page size there is no single "the
 * table's own `PAGE_LIMIT`" left for this constant to be. The value did not move; only
 * the justification stopped pointing at a constant that no longer exists.
 */
export const ROWS_PER_GROUP_MAX = 50;

/**
 * THE DISCOVERED CURRENCY CODE SET SIZES THE SELECT LIST, so it is a cardinality cap of
 * the same kind as the group cap (D2, D20).
 *
 * "Bounded by a `char(3)` domain" is a hope rather than a bound - it admits 17,576
 * values, and one dirty column with 40 codes across three CURRENCY footer columns is 120
 * aggregate expressions in one statement. Above the cap the aggregate REFUSES rather than
 * truncating, and the difference from the group cap is the one `csv-import-export.md` D35
 * draws: a truncated group list is a shortened list, but a dropped currency code is a
 * missing sum, and a footer that quietly omits the USD column is a wrong number rather
 * than a short one.
 */
export const CURRENCY_CODES_MAX = 8;

/**
 * How many aggregates one grouped read may request.
 *
 * NOT A NUMBER THIS MODULE'S DOCUMENT NAMES, and it is here because every other cap is:
 * the clamp is the schema's, in `zod`, and an uncapped array inside the 1 MB body limit
 * is tens of thousands of SQL expressions in one statement. Sized against
 * `SAVED_VIEW_MAX_COLUMNS` (100), because the widest legal footer is one operation per
 * pinned column and the per-group header aggregate is one more.
 */
export const GROUP_MAX_AGGREGATES = 101;

// --- date buckets (D13) -----------------------------------------------------

/**
 * THE GRANULARITY SET, CLOSED AT FIVE.
 *
 * Twenty ships eight. The three dropped are the cyclic ones - day-of-the-week,
 * month-of-the-year, quarter-of-the-year: they are seasonality analysis rather than list
 * organisation, they produce text keys that sort alphabetically unless you build an
 * explicit ordering CASE, and they then need locale translation at display time while the
 * key stays English. Three mechanisms for a question no screen in this CRM asks.
 */
export const DATE_GRANULARITIES = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const;
export type DateGranularity = (typeof DATE_GRANULARITIES)[number];
export const dateGranularitySchema = z.literal([...DATE_GRANULARITIES]);

/**
 * MONDAY, FIXED, AND NOT CONFIGURABLE IN V1 (Arif, 2026-08-16).
 *
 * `resolveRelativeDate` already computes a Monday week (`view.ts:152`), and a `this_week`
 * filter that disagreed with a weekly bucket over the same column would be the worst
 * possible bug: both are right and the screen is wrong. Twenty's Sunday and Saturday
 * options are simply not offered, so this is a constant rather than a wire field, a view
 * setting or an instance setting.
 */
export const GROUP_WEEK_START_DAY = 'MONDAY';

/** `GROUP_WEEK_START_DAY` as `Date#getUTCDay` reports it: 0 is Sunday. */
export const GROUP_WEEK_START_DAY_INDEX = 1;

/**
 * A TIMEZONE THE RUNTIME'S OWN TZ DATABASE KNOWS, checked by CONSTRUCTING a formatter
 * rather than by a regex, which would accept `Europe/Berlim`. It is the technique
 * `config.ts`'s `isKnownTimeZone` already uses for the instance clock.
 *
 * IT IS THE FIRST OF TWO GATES, NOT THE ONLY ONE. PostgreSQL resolves `AT TIME ZONE`
 * against its own `pg_timezone_names` view, and the two sets are not identical, so the
 * server checks that list as well before binding the parameter (D13, obligation 7). This
 * gate is the one the BROWSER can run, and it is the one `bucketBounds` needs anyway: a
 * name `Intl` does not know is a name this file cannot compute an instant for.
 */
export function isSupportedTimeZone(name: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

// --- the wire type (D1) -----------------------------------------------------

export class RecordGroupError extends Error {
  override readonly name = 'RecordGroupError';
}

/**
 * ONE LEVEL OF GROUPING.
 *
 * `granularity` is present exactly for the two bucketed types (D13, `groupable:
 * 'bucketed'` in the registry). `timeZone` is REQUIRED beside it for a DATETIME and
 * REFUSED for a DATE: a `date` column has no instant to shift, and accepting a zone there
 * would let two callers get two different bucketings of the same immutable value.
 *
 * The field's TYPE is not on the wire, so the schema can only enforce the half that does
 * not need metadata - a zone belongs to a bucket. The type-dependent half (a DATETIME
 * must carry one, a DATE must not) is checked where the field type is known.
 */
export interface GroupDimension {
  readonly fieldId: string;
  /** DATE and DATETIME only. Absent means "group by the stored value". */
  readonly granularity?: DateGranularity | undefined;
  /** An IANA zone name, sent by the BROWSER on every grouped read. No server default. */
  readonly timeZone?: string | undefined;
}

export const groupDimensionSchema = z
  .strictObject({
    fieldId: z.uuid(),
    granularity: dateGranularitySchema.optional(),
    timeZone: z
      .string()
      .min(1)
      .refine(isSupportedTimeZone, 'not an IANA time zone this runtime knows')
      .optional(),
  })
  .refine((dimension) => dimension.timeZone === undefined || dimension.granularity !== undefined, {
    message: 'a timezone belongs to a date bucket, and a bucket needs a granularity',
  });

/**
 * ONE REQUESTED AGGREGATE.
 *
 * `count` is the group's own `count(*)`: it names no field, passes no field permission
 * check, and is bounded by the object check and the scope predicate like any row count
 * (D7, D12). Every other operation names exactly one field, and the schema enforces the
 * biconditional so neither shape can arrive half-formed.
 */
export interface AggregateRequest {
  readonly op: AggregateOp;
  /** Absent for `count`, required for the other nine. */
  readonly fieldId?: string | undefined;
}

export const aggregateRequestSchema = z
  .strictObject({
    op: aggregateOpSchema,
    fieldId: z.uuid().optional(),
  })
  .refine((request) => (request.op === 'count') === (request.fieldId === undefined), {
    message: "`count` is the group's own row count and names no field; the others name one",
  });

/**
 * COMPOSITION, NOT EXTENSION (D1).
 *
 * The filter, the sort, the select and the soft-delete flag are literally the same
 * `RecordQuery` object with the same `zod` schema, so there is no second vocabulary to
 * keep in step; the group-specific keys sit OUTSIDE it, where the three surfaces that
 * consume `recordQuerySchema` today - `POST /api/query`, the MCP `query_records` tool and
 * the CSV export envelope - cannot see them. It is the separation `restrictToIds` already
 * gets: a caller-shaping input passed BESIDE the query rather than inside it.
 *
 * There is NO `groupOrderBy`, and its absence is a decision rather than an omission.
 * Group order is DERIVED from the dimension (D11, D17), and the deferred
 * order-by-an-aggregate feature is what would give such a field semantics - until then it
 * does not exist rather than existing and meaning nothing (section 12).
 *
 * ONE HONEST WART: `recordQuerySchema` requires `select` to be non-empty, and an
 * aggregates-only call has nothing to select. The select stays required and is NOT inert
 * when `rowsPerGroup` is 0 - `planRecordQuery` runs `requireFieldAccess(..., 'select')`
 * over every entry, so an aggregates-only caller must name a field it is permitted to
 * READ and the grouped statement simply does not project it. That is a stricter check
 * than the caller asked for rather than a looser one, and it costs one field id.
 */
export interface RecordGroupQuery {
  readonly query: RecordQuery;
  readonly groupBy: readonly GroupDimension[];
  readonly aggregates?: readonly AggregateRequest[] | undefined;
  readonly groupLimit?: number | undefined;
  /** 0 means aggregates only: no rows are seeded into the groups. */
  readonly rowsPerGroup?: number | undefined;
}

/**
 * The caps are the SCHEMA's, exactly as `limit` is on `recordQuerySchema` - so a value
 * above one is REFUSED rather than clamped silently (obligation 32). Twenty's own group
 * limit has no upper clamp at all.
 */
export const recordGroupQuerySchema = z.strictObject({
  /** Validated by `parseRecordQuery`, which runs its depth bound BEFORE zod. */
  query: z.unknown(),
  /**
   * ZERO IS LEGAL AND MEANS "THE TOTAL" (D30, amended by the owner on 2026-09-06).
   *
   * An empty `groupBy` returns exactly ONE bucket - the aggregates over the filtered set -
   * with the same response shape and the same access rules as any other grouped read. It
   * is what a big-number report is, and the alternative was a second aggregate route
   * answering the same question through a second code path.
   *
   * The group CAP is unchanged and still counts groups rather than dimensions: one bucket
   * is one group, so a zero-dimension read is trivially under every limit.
   */
  groupBy: z.array(groupDimensionSchema).min(0).max(GROUP_MAX_DIMENSIONS),
  aggregates: z.array(aggregateRequestSchema).max(GROUP_MAX_AGGREGATES).optional(),
  groupLimit: z.int().min(1).max(GROUP_MAX_LIMIT).optional(),
  rowsPerGroup: z.int().min(0).max(ROWS_PER_GROUP_MAX).optional(),
});

/**
 * The envelope, then the query through the existing parser - the shape
 * `parseCreateExportRequest` already uses, and for the same reason: embedding
 * `recordQuerySchema` directly would skip the raw-body depth bound `parseRecordQuery`
 * runs first, and that bound exists because the overflow happens inside zod's own
 * `z.lazy` descent and would surface as a 500 instead of a 400.
 */
export function parseRecordGroupQuery(input: unknown): RecordGroupQuery {
  const envelope = recordGroupQuerySchema.parse(input);
  return {
    query: parseRecordQuery(envelope.query),
    groupBy: envelope.groupBy,
    aggregates: envelope.aggregates,
    groupLimit: envelope.groupLimit,
    rowsPerGroup: envelope.rowsPerGroup,
  };
}

/** Effective group cap for a grouped read. `recordQueryLimit`'s twin. */
export function groupQueryLimit(query: RecordGroupQuery): number {
  return query.groupLimit ?? GROUP_DEFAULT_LIMIT;
}

/** Effective preview size. Zero is a real answer and is not replaced by the default. */
export function rowsPerGroupOf(query: RecordGroupQuery): number {
  return query.rowsPerGroup ?? ROWS_PER_GROUP_DEFAULT;
}

// --- group identity (D5, D16) -----------------------------------------------

/**
 * A GROUP KEY IS A TYPED TUPLE. NEVER A JOINED STRING.
 *
 * Twenty joins stringified values with `|`, and two failures follow from that one line in
 * THEIR product: `String(null)` is the literal `"null"`, which collides with a text value
 * that happens to be `"null"`; and `|` is legal inside a text value, so `["a|b", "c"]` and
 * `["a", "b|c"]` are the same key. Neither is reachable in ours today, because every
 * dimension D11 admits stores a generated id, a uuid, a boolean or a date - saying so is
 * what makes this decision honest. The tuple is bought against three other things:
 *
 *  1. THE DIMENSION TYPES THAT ARE COMING. MULTI_SELECT via `unnest`, a NUMBER histogram
 *     bin and TEXT are all values a string codec would have to encode, and a bin is a
 *     RANGE - the one shape a separator splits by accident. A key format cannot be
 *     changed later without invalidating every collapse-state map and React key in the
 *     app, so it is bought before it is needed or not at all.
 *  2. THE CURRENCY CODE. An earlier draft carried it as a hidden extra dimension; there
 *     is no `char(3)` member here, which is what forced the correction into the open
 *     instead of into the wire format (D2).
 *  3. NULL IS A KIND, NOT A VALUE. A string key cannot separate "no value" from a real
 *     value without a reserved sentinel, and the board has already had to invent one
 *     (`NO_OPTION_COLUMN_ID`). A typed tuple makes the sentinel unnecessary rather than
 *     making it safe.
 *
 * A `bucket` part carries its GRANULARITY as well as its start, because "2026-08-01 at
 * MONTH" and "2026-08-01 at DAY" are different groups and a start date alone cannot say
 * which. It deliberately does NOT carry the timezone: the zone is a property of the
 * DIMENSION, it is already in `GroupDimension`, and duplicating it into every key would
 * let a key and its dimension disagree.
 */
export type GroupKeyPart =
  | { readonly kind: 'null' }
  | { readonly kind: 'option'; readonly optionId: string }
  | { readonly kind: 'boolean'; readonly value: boolean }
  | { readonly kind: 'relation'; readonly recordId: string }
  | { readonly kind: 'bucket'; readonly granularity: DateGranularity; readonly start: string };

export type GroupKey = readonly GroupKeyPart[];

/** The one arm `bucketBounds` takes, named so a caller cannot pass a whole key part. */
export type GroupBucketKeyPart = Extract<GroupKeyPart, { kind: 'bucket' }>;

export const groupKeyPartSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('null') }),
  z.strictObject({
    kind: z.literal('option'),
    optionId: z.string().regex(SELECT_OPTION_ID_PATTERN),
  }),
  z.strictObject({ kind: z.literal('boolean'), value: z.boolean() }),
  z.strictObject({ kind: z.literal('relation'), recordId: z.uuid() }),
  z.strictObject({
    kind: z.literal('bucket'),
    granularity: dateGranularitySchema,
    /** The bucket START, as a wall-clock date in the dimension's timezone. */
    start: z.iso.date(),
  }),
]);

export const groupKeySchema = z.array(groupKeyPartSchema).min(1).max(GROUP_MAX_DIMENSIONS);

/**
 * THE ONE ENCODING. base64url over the tuple's JSON, the way a query cursor is produced.
 *
 * Hand-rolled over `TextEncoder` rather than reached for from `node:buffer`, because
 * `shared` is isomorphic (INV-11) and this function runs in the browser, where a React
 * key and a collapse-state map are what need it.
 */
export function encodeGroupKey(key: GroupKey): string {
  const bytes = new TextEncoder().encode(JSON.stringify(key));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

/**
 * The inverse, and it VALIDATES rather than casting: an encoded key travels through a
 * cache lookup and a URL, so what comes back is untrusted input and a malformed one must
 * fail here rather than deep inside a filter.
 */
export function decodeGroupKey(encoded: string): GroupKey {
  let document: unknown;
  try {
    const binary = atob(encoded.replaceAll('-', '+').replaceAll('_', '/'));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    document = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new RecordGroupError(`"${encoded}" is not an encoded group key`);
  }
  const parsed = groupKeySchema.safeParse(document);
  if (!parsed.success) {
    throw new RecordGroupError(
      `"${encoded}" decodes to something that is not a group key: ${parsed.error.issues
        .map((issue) => `${issue.path.join('.')} ${issue.message}`)
        .join('; ')}`,
    );
  }
  return parsed.data;
}

// --- the result (D2, D12, D18) ----------------------------------------------

/**
 * ONE CURRENCY CODE'S FIGURE INSIDE ONE AGGREGATE.
 *
 * Micros, exactly as the column stores them (`CURRENCY_MICROS_PER_UNIT`). `avg` divides,
 * so its micros are fractional; every other operation returns whole ones. Null means the
 * code has no non-null rows in this group, which renders an em dash like any other value
 * operation over nothing (D12 semantic decision 3).
 */
export interface CurrencyAggregateAmount {
  readonly currencyCode: string;
  readonly amountMicros: number | null;
}

/**
 * ONE AGGREGATE'S ANSWER FOR ONE GROUP.
 *
 * `value` covers eight of the ten operations and both non-currency arms of the other two:
 * counts are numbers, `sum`/`avg` over a NUMBER are numbers, `min`/`max` over a DATE or
 * DATETIME are ISO strings, and null means "no rows, or no non-null values". The FORBIDDEN
 * case cannot reach here at all - a denied dimension or aggregate field refuses the whole
 * request (D8) - so a null in a response has exactly one meaning, unlike `StatTile`'s,
 * which reserves it for "you may not see this".
 *
 * `currency` is the other arm, and it is one row per group rather than one row per
 * `(group, code)` pair: `sum(amount_micros)` across mixed codes is arithmetically
 * meaningless, so a CURRENCY aggregate is a set of CONDITIONAL aggregates, one
 * `filter (where currency_code = $k)` expression per discovered code, all in the same row
 * (D2, D12 semantic decision 4). Nothing is ever summed across codes; the cell renders one
 * line per code (Arif, 2026-08-16).
 */
export type AggregateValue =
  | { readonly kind: 'value'; readonly value: number | string | null }
  | { readonly kind: 'currency'; readonly amounts: readonly CurrencyAggregateAmount[] };

/**
 * WHETHER ONE AGGREGATE SPLITS BY CURRENCY CODE.
 *
 * The split follows the ARITHMETIC, not the field type alone: `countValues` over a
 * CURRENCY column counts rows and mixes no units, while `sum`, `avg`, `min` and `max` add
 * or order amounts and would be comparing EUR against USD. The value family is exactly
 * those four (`isCountAggregateOp`), so the rule is read off that split rather than
 * written down a second time.
 */
export function aggregateSplitsByCurrency(fieldType: FieldType, op: AggregateOp): boolean {
  return fieldType === 'CURRENCY' && !isCountAggregateOp(op);
}

/**
 * The numbers one `GROUP BY` row carries: the group's own `count(*)` (the `_n` column)
 * and one entry per requested aggregate, in request order.
 *
 * The GRAND TOTAL is the same shape, produced by the same statement with an empty
 * `groupBy` (D2). That is deliberate and is what makes the footer's parts-and-whole claim
 * checkable: it is also the comparand a synthesised empty group must equal, operation by
 * operation, when the filter matches zero rows (D29, obligation 40).
 */
export interface RecordGroupTotals {
  readonly count: number;
  readonly aggregates: readonly AggregateValue[];
}

export interface RecordGroup extends RecordGroupTotals {
  readonly key: GroupKey;
  /**
   * Index-aligned with `key`, and non-null only where the server resolved something the
   * client could not: a RELATION dimension's target label. Null everywhere else, and null
   * for a relation whose target the caller may not read - the label is OMITTED rather
   * than refused, because refusing would answer 200 or 403 depending on the DATA (D8).
   *
   * A SELECT label comes from metadata the browser already holds, and a bucket label is
   * formatted in the browser's own locale (D13): group on stable keys, translate on
   * output.
   */
  readonly labels: readonly (string | null)[];
  /** The seeded preview rows. Empty when `rowsPerGroup` is 0 (D19). */
  readonly records: readonly QueryRecord[];
}

export interface RecordGroupResult {
  readonly groups: readonly RecordGroup[];
  /**
   * More groups exist beyond the cap. It does NOT say how many: counting the distinct
   * values would be a second full scan, so the sentence on screen is "the first 50
   * groups", never "50 of 312" (D20).
   */
  readonly hasMoreGroups: boolean;
  readonly total: RecordGroupTotals;
  /**
   * The discovered codes, ordered, present only when at least one CURRENCY aggregate was
   * requested. Ordering them is what keeps the footer's lines from reshuffling between
   * renders (D2).
   */
  readonly currencyCodes?: readonly string[] | undefined;
}

// --- bucket bounds (D13, D19) -----------------------------------------------

/**
 * A BUCKET'S HALF-OPEN BOUNDS, DISCRIMINATED ON THE DIMENSION'S FIELD TYPE.
 *
 * The union is what stops the wrong operand being built. `requireDate` demands
 * `YYYY-MM-DD` and `requireDatetime` demands an ISO-8601 timestamp WITH an offset, so a
 * single `{start, end}` of instants would compile and then 422 on every DATE bucket's
 * "show more".
 *
 * Both arms are consumed as `and(gte <start>, lt <end>)`, never as `between`:
 * `temporalBetween` compiles to `>=` AND `<=`, inclusive on both ends, so a record sitting
 * exactly on the next bucket's start would be returned by both adjacent groups' row lists
 * while `date_trunc` counted it in exactly one (D19).
 */
export type BucketBounds =
  | { readonly kind: 'date'; readonly start: string; readonly end: string }
  | { readonly kind: 'datetime'; readonly start: string; readonly end: string };

/**
 * THE BUCKET KEY BACK INTO A FILTER OPERAND, AND THE WHOLE DST ANSWER IS THE ORDERING.
 *
 * The next bucket start is computed by adding one CALENDAR unit to the WALL CLOCK and
 * only then converting to an instant - never by adding 24 hours, 7 days or 30 days to an
 * instant. `date_trunc('day', ts at time zone 'Europe/Berlin')` on 2026-10-25 covers 25
 * hours; a caller that computed `start + 1 day` as a duration would produce a bound one
 * hour short, and an hour of records would be counted by the group header and missing
 * from its rows. The same failure lands on the last week and the last month of every DST
 * transition (obligation 38).
 *
 * THE ARM FOLLOWS THE DIMENSION, NEVER THE CALLER'S CHOICE, and the discriminator is the
 * TIMEZONE: a DATE dimension carries none and its bounds are wall-clock dates with no
 * conversion at all, while a DATETIME dimension must carry one (D13). That biconditional
 * is what makes "the timezone is present" and "the field is a DATETIME" the same
 * question.
 */
export function bucketBounds(key: GroupBucketKeyPart, dimension: GroupDimension): BucketBounds {
  if (dimension.granularity !== key.granularity) {
    throw new RecordGroupError(
      `a ${key.granularity} bucket key does not belong to a dimension bucketed by ${
        dimension.granularity ?? 'nothing'
      }`,
    );
  }
  const start = bucketStartMs(key);
  const end = nextBucketStartMs(start, key.granularity);
  const { timeZone } = dimension;
  if (timeZone === undefined) {
    return { kind: 'date', start: isoDateOf(start), end: isoDateOf(end) };
  }
  return {
    kind: 'datetime',
    start: instantOfWallClock(start, timeZone),
    end: instantOfWallClock(end, timeZone),
  };
}

/**
 * The start as milliseconds of UTC MIDNIGHT - a carrier for a wall clock, not an instant.
 * No arithmetic below this line touches a real offset until `instantOfWallClock` does.
 *
 * ALIGNMENT IS CHECKED, because an encoded key is untrusted input and a start that is not
 * a bucket boundary produces bounds that no group's count was ever computed over: a
 * "MONTH" bucket starting on the 15th would return the rows of two half-months under a
 * header claiming one.
 */
/**
 * THE CALENDAR, EXPORTED ONCE (`reports.md` T4).
 *
 * Filling the buckets a grouped read did not return means walking the calendar, and the
 * calendar is already written down here - Monday weeks, month starts, quarter starts. A
 * second implementation in the Reports module would be a second answer to "what is the
 * week after this one", and the first bug it produced would be a chart whose gap is in a
 * different place from the group header above it.
 *
 * `alignBucketStart` snaps any date to the start of the bucket that contains it;
 * `nextBucketStart` returns the start of the following one. Both take and return a
 * `YYYY-MM-DD` wall-clock date, which is what a bucket key carries.
 */
export function alignBucketStart(isoDate: string, granularity: DateGranularity): string {
  const parsed = Date.parse(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed)) {
    throw new RecordGroupError(`"${isoDate}" is not a date that exists in the calendar`);
  }
  const date = new Date(parsed);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  switch (granularity) {
    case 'DAY':
      return isoDateOf(parsed);
    case 'WEEK': {
      // Monday, fixed (GROUP_WEEK_START_DAY_INDEX): Sunday is 0, so it is six days back.
      const weekday = date.getUTCDay();
      const back = (weekday - GROUP_WEEK_START_DAY_INDEX + 7) % 7;
      return isoDateOf(utcMidnight(year, month, day - back));
    }
    case 'MONTH':
      return isoDateOf(utcMidnight(year, month, 1));
    case 'QUARTER':
      return isoDateOf(utcMidnight(year, month - (month % 3), 1));
    case 'YEAR':
      return isoDateOf(utcMidnight(year, 0, 1));
    default:
      return assertNever(granularity, 'alignBucketStart');
  }
}

/** The start of the bucket after the one beginning at `isoDate`. */
export function nextBucketStart(isoDate: string, granularity: DateGranularity): string {
  const parsed = Date.parse(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed)) {
    throw new RecordGroupError(`"${isoDate}" is not a date that exists in the calendar`);
  }
  return isoDateOf(nextBucketStartMs(parsed, granularity));
}

function bucketStartMs(key: GroupBucketKeyPart): number {
  const parsed = Date.parse(`${key.start}T00:00:00.000Z`);
  if (Number.isNaN(parsed) || isoDateOf(parsed) !== key.start) {
    throw new RecordGroupError(`"${key.start}" is not a date that exists in the calendar`);
  }
  const date = new Date(parsed);
  const aligned = ((): boolean => {
    switch (key.granularity) {
      case 'DAY':
        return true;
      case 'WEEK':
        return date.getUTCDay() === GROUP_WEEK_START_DAY_INDEX;
      case 'MONTH':
        return date.getUTCDate() === 1;
      case 'QUARTER':
        return date.getUTCDate() === 1 && date.getUTCMonth() % 3 === 0;
      case 'YEAR':
        return date.getUTCDate() === 1 && date.getUTCMonth() === 0;
      default:
        return assertNever(key.granularity, 'bucketStartMs');
    }
  })();
  if (!aligned) {
    throw new RecordGroupError(
      `"${key.start}" is not the start of a ${key.granularity} bucket (weeks start ${GROUP_WEEK_START_DAY})`,
    );
  }
  return parsed;
}

/** One CALENDAR unit later, still on the wall clock. */
function nextBucketStartMs(startMs: number, granularity: DateGranularity): number {
  const date = new Date(startMs);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  switch (granularity) {
    case 'DAY':
      return utcMidnight(year, month, day + 1);
    case 'WEEK':
      return utcMidnight(year, month, day + 7);
    case 'MONTH':
      return utcMidnight(year, month + 1, day);
    case 'QUARTER':
      return utcMidnight(year, month + 3, day);
    case 'YEAR':
      return utcMidnight(year + 1, month, day);
    default:
      return assertNever(granularity, 'nextBucketStartMs');
  }
}

/**
 * `Date.UTC` shifts a two-digit year into the 1900s, so the year is set explicitly. Month
 * and day overflow deliberately: month 12 is January of the next year, and day 32 is the
 * 1st of the next month, which is the whole point of doing this on a calendar.
 */
function utcMidnight(year: number, month: number, day: number): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month, day);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

function isoDateOf(wallClockMs: number): string {
  return new Date(wallClockMs).toISOString().slice(0, 10);
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = zoneFormatters.get(timeZone);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    throw new RecordGroupError(`"${timeZone}" is not an IANA time zone this runtime knows`);
  }
  zoneFormatters.set(timeZone, formatter);
  return formatter;
}

/**
 * How far the zone's wall clock is ahead of UTC AT THIS INSTANT, in milliseconds. Read off
 * the runtime's tz database rather than from a table, which is what makes every DST rule -
 * including the ones that change next year - somebody else's problem.
 *
 * Truncated to whole minutes: a handful of zones carry seconds in their pre-1900 offsets,
 * and an ISO-8601 offset has no room for them.
 */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(instantMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type);
    return found === undefined ? 0 : Number(found.value);
  };
  const wallMs = utcWallClock(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour'),
    read('minute'),
    read('second'),
  );
  return Math.trunc((wallMs - instantMs) / 60_000) * 60_000;
}

function utcWallClock(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month, day);
  date.setUTCHours(hour, minute, second, 0);
  return date.getTime();
}

/**
 * ONE WALL CLOCK IN ONE ZONE -> THE INSTANT IT NAMES, as ISO-8601 with that zone's own
 * offset.
 *
 * Two passes, because the offset depends on the instant we are trying to find. The first
 * guess reads the offset at the wall clock taken as UTC; if the corrected instant sits in
 * the same offset it is the answer. If it does not, the wall clock is near a transition,
 * and the second guess is tried: when THAT one is self-consistent it is the answer.
 *
 * When neither is, the local time does not exist - a spring-forward gap swallowed it - and
 * the first candidate is returned, which is the instant the PRE-transition offset names.
 * That is the resolution PostgreSQL's own `AT TIME ZONE` gives a skipped timestamp, and
 * agreeing with the database is the property that matters: the bounds and the count have
 * to be talking about the same moment.
 *
 * An AMBIGUOUS local time - a fall-back that crosses midnight, which a few zones have -
 * resolves to whichever candidate is self-consistent, and that is the EARLIER of the two
 * whenever the first guess lands before the transition. Not asserted against PostgreSQL:
 * no bucket this instance produces in `Europe/Berlin` is ambiguous, because its fall-back
 * is at 03:00 local.
 */
function instantOfWallClock(wallClockMs: number, timeZone: string): string {
  const firstOffset = zoneOffsetMs(wallClockMs, timeZone);
  const candidate = wallClockMs - firstOffset;
  const secondOffset = zoneOffsetMs(candidate, timeZone);
  let instantMs = candidate;
  if (secondOffset !== firstOffset) {
    const corrected = wallClockMs - secondOffset;
    if (zoneOffsetMs(corrected, timeZone) === secondOffset) instantMs = corrected;
  }
  const offset = zoneOffsetMs(instantMs, timeZone);
  const localText = new Date(instantMs + offset).toISOString().slice(0, 19);
  return `${localText}${offsetText(offset)}`;
}

function offsetText(offsetMs: number): string {
  const minutes = Math.trunc(offsetMs / 60_000);
  const absolute = Math.abs(minutes);
  const hh = String(Math.floor(absolute / 60)).padStart(2, '0');
  const mm = String(absolute % 60).padStart(2, '0');
  return `${minutes < 0 ? '-' : '+'}${hh}:${mm}`;
}
